import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { useAuth } from '@/contexts/AuthContext';
import { notificationsAPI, mediaUrl } from '@/lib/api';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from '@/components/ui/command';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  LayoutDashboard, Users, CalendarCheck, HandCoins, BookOpen,
  Video, Phone, MessageCircleHeart, Sparkles, Calendar,
  Megaphone, UsersRound, BarChart3, Settings, LogOut,
  Menu, X, Church, ChevronDown, Bot, Wand2, UserCheck,
  ClipboardList, Target, HandHeart, Home, ListOrdered,
  Mail, Bell, CreditCard, VideoIcon, FileBarChart, Zap, Package, Globe, Puzzle, Flame, Headphones,
  Sun, Moon, Search, BellRing
} from 'lucide-react';
import { useTranslation, SUPPORTED_LANGUAGES } from '@/lib/i18n';

function getRolePortal(role) {
  switch (role) {
    case 'super_admin': return '/dashboard';
    case 'admin': return '/admin-portal';
    case 'pastor': return '/pastor-portal';
    case 'leader': return '/leader-portal';
    default: return '/my-portal';
  }
}

const navSections = [
  {
    label: 'Main',
    items: [
      { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['all'], dynamicHome: true },
      { path: '/my-portal', icon: Home, label: 'My Portal', roles: ['all'] },
      { path: '/notifications', icon: Bell, label: 'Notifications', roles: ['all'] },
      { path: '/chat', icon: Mail, label: 'Chat & Messages', roles: ['all'] },
    ]
  },
  {
    label: 'AI Tools',
    items: [
      { path: '/ai-assistant', icon: Bot, label: 'AI Assistant', roles: ['all'] },
      { path: '/ai-analytics', icon: BarChart3, label: 'AI Analytics', roles: ['super_admin', 'admin', 'pastor'] },
      { path: '/sermon-tools', icon: Wand2, label: 'Sermon & Study Tools', roles: ['super_admin', 'admin', 'pastor', 'leader'] },
    ]
  },
  {
    label: 'People',
    items: [
      { path: '/members', icon: Users, label: 'Members', roles: ['super_admin', 'admin', 'pastor', 'leader'] },
      { path: '/engagement', icon: Target, label: 'Engagement Scores', roles: ['super_admin', 'admin', 'pastor'] },
      { path: '/visitors', icon: UserCheck, label: 'Visitors', roles: ['super_admin', 'admin', 'pastor', 'leader'] },
      { path: '/families', icon: Home, label: 'Families', roles: ['super_admin', 'admin', 'pastor', 'leader'] },
      { path: '/volunteers', icon: HandHeart, label: 'Volunteers', roles: ['super_admin', 'admin', 'pastor', 'leader'] },
      { path: '/groups', icon: UsersRound, label: 'Groups & Ministries', roles: ['super_admin', 'admin', 'pastor', 'leader'] },
      { path: '/ministries', icon: Church, label: 'Ministries/Depts', roles: ['super_admin', 'admin', 'pastor'] },
    ]
  },
  {
    label: 'Worship',
    items: [
      { path: '/attendance', icon: CalendarCheck, label: 'Attendance', roles: ['super_admin', 'admin', 'pastor', 'leader'] },
      { path: '/check-in', icon: Target, label: 'Event Check-In', roles: ['all'] },
      { path: '/service-planner', icon: ListOrdered, label: 'Service Planner', roles: ['super_admin', 'admin', 'pastor'] },
      { path: '/live-stream', icon: VideoIcon, label: 'Live Stream', roles: ['all'] },
      { path: '/media', icon: Video, label: 'Media & Sermons', roles: ['all'] },
      { path: '/podcasts', icon: Headphones, label: 'Podcasts', roles: ['all'] },
      { path: '/bible', icon: BookOpen, label: 'Bible', roles: ['all'] },
      { path: '/events', icon: Calendar, label: 'Events', roles: ['all'] },
    ]
  },
  {
    label: 'Giving',
    items: [
      { path: '/giving', icon: HandCoins, label: 'Tithes & Offerings', roles: ['all'] },
      { path: '/online-giving', icon: CreditCard, label: 'Online Giving', roles: ['all'] },
      { path: '/donor-analytics', icon: BarChart3, label: 'Donor Analytics', roles: ['super_admin', 'admin', 'pastor'] },
      { path: '/pledges', icon: Target, label: 'Pledges', roles: ['super_admin', 'admin', 'pastor'] },
    ]
  },
  {
    label: 'Care',
    items: [
      { path: '/follow-ups', icon: ClipboardList, label: 'Follow-ups & Tasks', roles: ['super_admin', 'admin', 'pastor', 'leader'] },
      { path: '/appointments', icon: Calendar, label: 'Appointments', roles: ['all'] },
      { path: '/consultations', icon: Phone, label: 'Consultations', roles: ['all'] },
      { path: '/prayer-requests', icon: MessageCircleHeart, label: 'Prayer Requests', roles: ['all'] },
      { path: '/prophecy', icon: Flame, label: 'Prophecy', roles: ['all'] },
      { path: '/spiritual-growth', icon: Target, label: 'Spiritual Growth', roles: ['all'] },
      { path: '/forum', icon: MessageCircleHeart, label: 'Community Forum', roles: ['all'] },
      { path: '/testimonies', icon: Sparkles, label: 'Testimonies', roles: ['all'] },
      { path: '/announcements', icon: Megaphone, label: 'Announcements', roles: ['all'] },
    ]
  },
  {
    label: 'Admin',
    items: [
      { path: '/users', icon: Users, label: 'User Management', roles: ['super_admin', 'admin'] },
      { path: '/assets', icon: Package, label: 'Assets/Inventory', roles: ['super_admin', 'admin'] },
      { path: '/campaigns', icon: Megaphone, label: 'Campaigns', roles: ['super_admin', 'admin', 'pastor'] },
      { path: '/push', icon: BellRing, label: 'Push Notifications', roles: ['super_admin', 'admin'] },
      { path: '/documents', icon: BookOpen, label: 'Documents', roles: ['all'] },
      { path: '/reports', icon: BarChart3, label: 'Reports', roles: ['super_admin', 'admin', 'pastor'] },
      { path: '/reports-builder', icon: FileBarChart, label: 'Report Builder', roles: ['super_admin', 'admin', 'pastor'] },
      { path: '/automation', icon: Zap, label: 'Automation', roles: ['super_admin', 'admin'] },
      { path: '/integrations', icon: Puzzle, label: 'Integrations', roles: ['super_admin', 'admin'] },
      { path: '/admin-tools', icon: Package, label: 'Admin Tools', roles: ['super_admin', 'admin'] },
      { path: '/website-cms', icon: Globe, label: 'Website CMS', roles: ['super_admin'] },
      { path: '/settings', icon: Settings, label: 'Settings', roles: ['super_admin'] },
    ]
  },
];

export default function AppShell({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const { user, logout } = useAuth();
  const { t, lang, setLanguage } = useTranslation();
  const { theme, setTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const userInitials = user ? `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase() : 'U';
  const homePath = getRolePortal(user?.role);

  const canSee = (roles) => {
    if (roles.includes('all')) return true;
    return roles.includes(user?.role);
  };

  const resolvePath = (item) => (item.dynamicHome ? homePath : item.path);

  const flatItems = useMemo(
    () => navSections.flatMap((s) => s.items.filter((i) => canSee(i.roles)).map((i) => ({ ...i, path: resolvePath(i) }))),
    [user] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const res = await notificationsAPI.unreadCount();
        setUnreadCount(res.data.count);
      } catch {}
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, []);

  // Cmd/Ctrl-K command palette
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const go = (path) => {
    setPaletteOpen(false);
    setSidebarOpen(false);
    navigate(path);
  };

  const isDark = theme === 'dark';
  const ThemeToggle = (props) => (
    <Button
      variant="ghost"
      size="icon"
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      onClick={() => setTheme(isDark ? 'light' : 'dark')}
      {...props}
    >
      {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
    </Button>
  );

  return (
    <div className="app-shell">
      {/* Command palette */}
      <CommandDialog open={paletteOpen} onOpenChange={setPaletteOpen}>
        <CommandInput placeholder="Search pages and actions..." />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>
          <CommandGroup heading="Navigate">
            {flatItems.map((item) => (
              <CommandItem key={item.path} value={`${item.label} ${item.path}`} onSelect={() => go(item.path)}>
                <item.icon className="w-4 h-4 mr-2" />
                {item.label}
              </CommandItem>
            ))}
          </CommandGroup>
          <CommandGroup heading="Quick actions">
            <CommandItem value="profile settings account" onSelect={() => go('/profile')}>
              <Users className="w-4 h-4 mr-2" /> My Profile
            </CommandItem>
            <CommandItem value="toggle theme dark light" onSelect={() => { setTheme(isDark ? 'light' : 'dark'); setPaletteOpen(false); }}>
              {isDark ? <Sun className="w-4 h-4 mr-2" /> : <Moon className="w-4 h-4 mr-2" />} Toggle {isDark ? 'light' : 'dark'} mode
            </CommandItem>
            <CommandItem value="logout sign out" onSelect={() => { setPaletteOpen(false); handleLogout(); }}>
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </CommandDialog>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="sidebar-overlay md:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`} data-testid="app-sidebar" aria-label="Main navigation">
        <div className="flex items-center gap-3 p-4 border-b border-border">
          <div className="w-9 h-9 rounded-lg bg-primary/20 flex items-center justify-center">
            <Church className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="font-display font-semibold text-sm tracking-tight">NaziriteOS</h1>
            <p className="text-xs text-muted-foreground">Enterprise CMS</p>
          </div>
          <Button variant="ghost" size="icon" className="ml-auto md:hidden" aria-label="Close menu" onClick={() => setSidebarOpen(false)}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="px-2 py-2 hidden md:block">
          <button
            onClick={() => setPaletteOpen(true)}
            className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm text-muted-foreground bg-muted/50 hover:bg-muted transition-colors"
            aria-label="Open command palette"
          >
            <Search className="w-4 h-4" />
            <span className="flex-1 text-left">Search...</span>
            <kbd className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-background border border-border">⌘K</kbd>
          </button>
        </div>

        <ScrollArea className="flex-1 py-2">
          {navSections.map(section => {
            const visibleItems = section.items.filter(item => canSee(item.roles));
            if (visibleItems.length === 0) return null;
            return (
              <div key={section.label}>
                <div className="nav-section-label">{section.label}</div>
                {visibleItems.map(item => {
                  const path = resolvePath(item);
                  return (
                    <Link
                      key={item.path}
                      to={path}
                      className={`nav-item mx-2 ${location.pathname === path ? 'active' : ''}`}
                      data-testid={`sidebar-nav-${item.path.slice(1)}-link`}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <item.icon className="w-4 h-4" />
                      <span className="flex-1">{item.label}</span>
                      {item.path === '/notifications' && unreadCount > 0 && (
                        <Badge className="bg-destructive text-destructive-foreground text-xs h-5 min-w-5 flex items-center justify-center rounded-full px-1.5">
                          {unreadCount}
                        </Badge>
                      )}
                    </Link>
                  );
                })}
              </div>
            );
          })}
        </ScrollArea>

        <div className="border-t border-border p-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-3 w-full p-2 rounded-lg hover:bg-secondary/70 transition-colors" data-testid="user-menu-trigger">
                <Avatar className="w-8 h-8">
                  {user?.avatar && <AvatarImage src={mediaUrl(user.avatar)} alt="" />}
                  <AvatarFallback className="bg-primary/20 text-primary text-xs font-semibold">{userInitials}</AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium truncate">{user?.first_name} {user?.last_name}</p>
                  <p className="text-xs text-muted-foreground capitalize">{user?.role?.replace('_', ' ')}</p>
                </div>
                <ThemeToggle className="h-8 w-8" onClick={(e) => { e.stopPropagation(); setTheme(isDark ? 'light' : 'dark'); }} />
                <ChevronDown className="w-3 h-3 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => navigate('/profile')}>Profile</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/notifications')}>Notifications {unreadCount > 0 && `(${unreadCount})`}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTheme(isDark ? 'light' : 'dark')}>
                {isDark ? <Sun className="w-4 h-4 mr-2" /> : <Moon className="w-4 h-4 mr-2" />} {isDark ? 'Light mode' : 'Dark mode'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <div className="px-2 py-1.5">
                <p className="text-xs text-muted-foreground mb-1 font-medium">{t('language') || 'Language'}</p>
                <div className="flex gap-1">
                  {SUPPORTED_LANGUAGES.map(l => (
                    <button key={l.code} onClick={() => setLanguage(l.code)}
                      className={`text-xs px-2 py-1 rounded ${lang === l.code ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'}`}
                      data-testid={`lang-${l.code}`}>
                      {l.code.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-button">
                <LogOut className="w-4 h-4 mr-2" /> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        {/* Top bar (mobile) */}
        <div className="sticky top-0 z-30 flex items-center gap-3 p-3 border-b border-border bg-background/80 backdrop-blur md:hidden">
          <Button variant="ghost" size="icon" aria-label="Open menu" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-button">
            <Menu className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2 flex-1">
            <Church className="w-5 h-5 text-primary" />
            <span className="font-display font-semibold text-sm">NaziriteOS</span>
          </div>
          <Button variant="ghost" size="icon" aria-label="Search" onClick={() => setPaletteOpen(true)}>
            <Search className="w-5 h-5" />
          </Button>
          <ThemeToggle />
          <Button variant="ghost" size="icon" className="relative" aria-label="Notifications" onClick={() => navigate('/notifications')}>
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-destructive text-destructive-foreground text-xs rounded-full flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </Button>
        </div>
        <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
}
